import {noop, omit} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as shippingApi from '../../../services/api/shipping'
import {formDataToPartialShippingRequest, shippingsToFormData} from '../../forms/shippings/utils'
import {toMixedValidationSchema, toTouchedFormData} from '../../utils/form'

import {ModalMeta, Shipping} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import ShippingsForm, {
  MixedFormData,
  SingleFormData,
  initialValues,
  validationSchema,
} from '../../forms/shippings/form'

type Props = {
  meta: ModalMeta
  shippings: Shipping[]
  onDone?: () => void
}

const EditShippingsModal = ({meta, shippings = [], onDone = noop}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const formik = useFormik<MixedFormData>({
    initialValues: {
      ...initialValues,
      ...shippingsToFormData(shippings),
    },
    validationSchema: toMixedValidationSchema(omit(validationSchema, ['countryCodes'])),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const request = formDataToPartialShippingRequest(touchedValues)

      try {
        const ids = shippings.map(({id}) => id)
        await shippingApi.patchMultiple(request, ids)

        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <Modal
      size="l"
      fullscreenMobile={meta.fullscreenMobile}
      headline={
        <Headline
          label="Edit shipping option"
          addendum={shippings.length > 1 && `(${shippings.length} items)`}
          size="l"
        />
      }
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <ShippingsForm formik={formik} />
    </Modal>
  )
}

export default EditShippingsModal
